import React, { useCallback, useState, useEffect } from 'react';
import { Box, Button, Grid, Select, MenuItem, Text, TextField } from '../../../ui';
import { DatePicker } from '../../../ui/components/DateTimePickers';
import { currencyFormatter } from '../../../pages/QuotePage/content/QuoteGrids/utils';
import { Dialog, LayoutBox } from '../../../components';
import { QuotesListView } from '../../QuotesListView/LegacyQuoteList';
import { useApi } from '../../../hooks';
import moment from 'moment-timezone';

const QuoteModal = (props) => {
  const { saveAggregationConfiguration } = useApi();
  const [quotesData, setQuotesData] = useState([]);
  const [selectedQuotes, setSelectedQuotes] = useState(new Set());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configName, setConfigName] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [loading, setLoading] = useState({ save: false, load: false, reset: false, mapping: false });

  const {
    onClose,
    onSave,
    onConfigChange,
    configurations,
    aggregationQuotes = [],
    onMapConfig,
    activePoliciesDate,
    onActivePoliciesDateChange,
  } = props;

  useEffect(() => {
    if (aggregationQuotes && aggregationQuotes.length > 0) {
      const quoteIds = new Set(aggregationQuotes.map((quote) => quote.id));
      setSelectedQuotes(quoteIds);

      const mappedQuotes = aggregationQuotes.map((quote) => ({
        quoteNumber: quote.quoteNumber,
        customer: quote.insured,
        coverageAt: quote.coverageAt,
        quoteId: quote.id,
        coveredValue: quote.coveredValue,
        netPremium: quote.netPremium,
        numLocations: quote.numLocations || quote?.locations.length || '',
        rate: ((quote.netPremium / quote.coveredValue) * 100).toFixed(4),
      }));
      setQuotesData(mappedQuotes);
    }
  }, [aggregationQuotes]);

  useEffect(() => {
    if (selectedConfig && selectedConfig.activePoliciesDate && onActivePoliciesDateChange) {
      onActivePoliciesDateChange(moment(selectedConfig.activePoliciesDate));
    }
  }, [selectedConfig, onActivePoliciesDateChange]);

  const columnDefs = [
    {
      headerName: 'Quote Number',
      field: 'quoteNumber',
      flex: 1,
      valueFormatter: (field) => field.value || '',
    },
    {
      headerName: 'Customer',
      field: 'customer',
      flex: 1,
      valueFormatter: (field) => field.value || '',
    },
    {
      headerName: 'Coverage At',
      field: 'coverageAt',
      flex: 1,
      valueFormatter: (field) => moment.utc(field?.value).format('YYYY-MM-DD') || '',
    },
    {
      headerName: 'Locations',
      field: 'numLocations',
      flex: 1,
      valueFormatter: (field) => field.value || '',
    },
    {
      headerName: 'TIV',
      field: 'coveredValue',
      flex: 1,
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: 'GWP',
      field: 'netPremium',
      flex: 1,
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: 'Rate',
      field: 'rate',
      flex: 1,
      valueFormatter: (field) => `$ ${field.value}` || '',
    },
    {
      headerName: 'Quote ID',
      field: 'quoteId',
      flex: 1,
      valueFormatter: (field) => field.value || '',
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 120,
    resizable: true,
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleQuoteSelect = (quote) => {
    setSelectedQuotes((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(quote.id)) {
        newSelected.delete(quote.id);
        setQuotesData((current) => current.filter((item) => item.quoteId !== quote.id));
      } else {
        newSelected.add(quote.id);
        const mappedQuote = {
          quoteNumber: quote.quoteNumber,
          customer: quote.insured,
          coverageAt: quote.coverageAt,
          quoteId: quote.id,
          coveredValue: quote.coveredValue,
          netPremium: quote.netPremium,
          numLocations: quote.numLocations,
          rate: ((quote.netPremium / quote.coveredValue) * 100).toFixed(4),
        };
        setQuotesData((current) => [...current, mappedQuote]);
      }
      return newSelected;
    });
  };

  const handleSaveConfig = useCallback(async () => {
    try {
      if (selectedQuotes.size === 0) {
        alert('Please select at least one quote');
        return;
      }
      if (!configName.trim()) {
        alert('Please enter a configuration name');
        return;
      }
      setShowSaveModal(false);
      setLoading((prev) => ({ ...prev, save: true }));
      const payload = {
        name: configName.trim(),
        quoteIds: Array.from(selectedQuotes),
        numberQuotes: selectedQuotes.size,
        activePoliciesDate: activePoliciesDate.valueOf(),
      };

      const config = await saveAggregationConfiguration(payload);
      if (config) {
        await onConfigChange(config);
        alert('Configuration saved successfully');
      }
      setConfigName('');
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  }, [selectedQuotes, saveAggregationConfiguration, onClose, onSave, activePoliciesDate, configName]);

  const handleLoadConfig = useCallback(async () => {
    if (!selectedConfig) {
      alert('Please select a configuration');
      return;
    }
    try {
      setShowConfigModal(false);
      setLoading((prev) => ({ ...prev, load: true }));
      await onConfigChange(selectedConfig);
    } catch (error) {
      console.error('Error loading configuration:', error);
      alert('Failed to load configuration');
    } finally {
      setLoading((prev) => ({ ...prev, load: false }));
    }
  }, [selectedConfig, onConfigChange]);

  const handleResetConfig = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, reset: true }));
      const config = configurations.find((config) => config?.name === '3/31/2025 Configuration');
      setSelectedConfig(config);
      await onConfigChange(config);
    } catch (error) {
      console.error('Error resetting configuration:', error);
      alert('Failed to reset configuration');
    } finally {
      setLoading((prev) => ({ ...prev, reset: false }));
    }
  }, [onConfigChange, configurations]);

  const handleConfigSelect = (config) => {
    if (!config) return;
    setSelectedConfig(config);
    onConfigChange(config);
  };

  const handleMapConfig = useCallback(async () => {
    if (!selectedConfig) {
      alert('Please save a configuration before mapping');
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, mapping: true }));
      await onMapConfig(selectedConfig, activePoliciesDate.valueOf());
    } catch (error) {
      console.error('Error mapping configuration:', error);
      alert('Failed to map configuration');
    } finally {
      setLoading((prev) => ({ ...prev, mapping: false }));
    }
  }, [onMapConfig, selectedConfig, activePoliciesDate]);

  if (!props?.isOpen) {
    return null;
  }

  return (
    <Box
      onClick={handleOverlayClick}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          top: 35,
        }}
      >
        <Box sx={{ flex: 'none' }}>
          <LayoutBox row justifyContent="space-between" alignItems="center" marginBottom="$4">
            <Box>
              <Text sx={{ margin: 0, color: '#2d3748', fontSize: '20px', fontWeight: 600 }}>Aggregate Configuration</Text>
            </Box>
            <Box border="1px solid #919EAB52" borderRadius={8} margin="$1">
              <Button
                color="$primary"
                variant="contained"
                size="medium"
                onPress={handleMapConfig}
                loading={loading.mapping}
                disabled={loading.mapping}
              >
                Map Configuration
              </Button>
            </Box>
          </LayoutBox>

          <Box sx={{ minHeight: '120px', maxHeight: '320px', height: 'auto', width: '100%', marginBottom: '20px', overflow: 'auto' }}>
            <Grid
              data={quotesData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              floatingFilter={false}
              shouldShowPagination={false}
              setGridReference={props.setGridReference}
            />
          </Box>

          <LayoutBox row justifyContent="flex-start" alignItems="center">
            <Text sx={{ justifyContent: 'start', fontWeight: 400, fontSize: '18px', marginRight: '12px', color: '$primary' }}>
              {selectedQuotes.size > 0 ? `Active Policies + ${selectedQuotes.size} Quotes:` : 'Active Policies as of:'}
            </Text>
            <TextField
              value={activePoliciesDate.format('MM/DD/YYYY')}
              readOnly
              onPress={() => setDatePickerOpen(true)}
              sx={{ width: 160, cursor: 'pointer', height: 60 }}
            />
            <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
              <DatePicker
                date={activePoliciesDate}
                onChange={(date) => {
                  onActivePoliciesDateChange(moment(date));
                  setDatePickerOpen(false);
                }}
              />
            </Dialog>
          </LayoutBox>
          <LayoutBox row justifyContent="flex-end" alignItems="center" marginBottom="$1">
            <Box border="1px solid #919EAB52" borderRadius={8} margin="$1">
              <Button
                label={loading.load ? '' : 'Load Configuration'}
                color="$primary"
                variant="text"
                size="medium"
                minWidth={80}
                minHeight={28}
                onPress={() => setShowConfigModal(true)}
                loading={loading.load}
                disabled={loading.load}
              ></Button>
            </Box>
            <Box border="1px solid #919EAB52" borderRadius={8} margin="$1">
              <Button
                label={loading.reset ? '' : 'Reset to 3/31/2025 configuration'}
                color="$primary"
                variant="text"
                size="medium"
                minWidth={80}
                minHeight={28}
                onPress={handleResetConfig}
                loading={loading.reset}
                disabled={loading.reset}
              ></Button>
            </Box>
            <Box border="1px solid #919EAB52" borderRadius={8} margin="$1">
              <Button
                label={loading.save ? '' : 'Save configuration'}
                color="$primary"
                variant="text"
                size="medium"
                minWidth={80}
                minHeight={28}
                onPress={() => setShowSaveModal(true)}
                loading={loading.save}
                disabled={loading.save}
              ></Button>
            </Box>
          </LayoutBox>
        </Box>

        <Box
          sx={{
            width: '100%',
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderTopColor: '#e2e8f0',
            flex: 1,
            minHeight: 0,
            position: 'relative',
          }}
        >
          <QuotesListView
            searchPath="/aggregation"
            batchSizeThreshold={99999}
            onSelect={handleQuoteSelect}
            scrollNode={false}
            useWindow={false}
            onEndReachedThreshold={0.2}
            scrollEventThrottle={100}
            removeClippedSubviews={false}
            selectedIds={selectedQuotes}
            styles={{
              root: { height: '100%', overflow: 'auto', paddingTop: '0px' },
              contentContainer: { minHeight: '100%' },
              item: { height: 'auto', minHeight: 100, border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '8px', width: '100%' },
            }}
          />
        </Box>

        {showConfigModal && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
            }}
          >
            <Box sx={{ background: 'white', padding: '20px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
              <Box sx={{ marginBottom: '20px' }}>
                <Select
                  value={selectedConfig?.aggregationId || ''}
                  placeholder="Select Configuration"
                  renderValue={(value) => {
                    if (!value) return '';
                    const config = configurations.find((config) => config.aggregationId === value);
                    return config?.name || '';
                  }}
                  sx={{ width: '100%', '& .MuiSelect-select': { padding: '8px 16px' } }}
                >
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.aggregationId} onPress={() => handleConfigSelect(config)}>
                      {config.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button color="$primary" variant="text" size="medium" onPress={() => setShowConfigModal(false)}>
                  Cancel
                </Button>
                <Button color="$primary" variant="contained" size="medium" onPress={handleLoadConfig}>
                  Load
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {showSaveModal && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
            }}
          >
            <Box sx={{ background: 'white', padding: '20px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
              <TextField
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="Enter configuration name"
                sx={{ width: '100%' }}
              />
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  color="$primary"
                  variant="text"
                  size="medium"
                  onPress={() => {
                    setShowSaveModal(false);
                    setConfigName('');
                  }}
                >
                  Cancel
                </Button>
                <Button color="$primary" variant="contained" size="medium" onPress={handleSaveConfig}>
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QuoteModal;
